import { createAsyncThunk } from "@reduxjs/toolkit";
import { db } from "@/lib/firebase";
import {
  collection,
  query,
  orderBy,
  limit,
  where,
  getDocs,
  addDoc,
  onSnapshot,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { toast } from "@/components/ui/use-toast";
import { partsIndex } from "../../lib/algoliaClient";
import { getSavedParts } from "./partsSlice";

// Async thunk: Fetches the 20 most recently added parts from Firestore, sorted by 'dateAdded' in descending order
export const fetchRecentParts = createAsyncThunk(
  "parts/fetchRecentParts",
  async (_, { rejectWithValue }) => {
    try {
      const partsCollection = collection(db, "parts");
      // Order by sortOrder ascending: 1 -> 2 -> 3
      const q = query(
        partsCollection,
        orderBy("sortOrder", "asc"),
        orderBy("dateAdded", "desc"),
        limit(20),
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error fetching recent parts:", error);
      toast({
        variant: "destructive",
        title: "Fetching Error",
        description: "Could not load recent parts due to a database error.",
      });
      return rejectWithValue(error.message);
    }
  },
);

export const searchParts = createAsyncThunk(
  "parts/searchParts",
  async (payload, { rejectWithValue }) => {
    const {
      searchTerm = "",
      searchPart = "",
      searchYear = "",
      searchMake = "",
      searchModel = "",
      searchLocation = "",
      page = 0,
    } = payload;

    try {
      const filters = [];

      // Apply filters if provided
      if (searchYear && searchYear !== "ANY_YEAR") {
        filters.push(`year:${searchYear}`);
      }
      if (searchMake && searchMake !== "ANY_MAKE") {
        filters.push(`make:${searchMake}`);
      }
      if (searchModel && searchModel !== "ANY_MODEL") {
        filters.push(`model:${searchModel}`);
      }
      if (searchLocation.trim()) {
        filters.push(`postalCode:${searchLocation.trim()}`);
      }

      // Choose search text
      const queryText = (searchTerm || searchPart).trim();

      // Step 1: Search in Algolia
      const response = await partsIndex.search(queryText, {
        filters: filters.join(" AND "),
        hitsPerPage: 10,
        page,
      });

      const ids = response.hits.map((hit) => hit.objectID);

      if (!ids.length) {
        return {
          results: [],
          queryDisplay: queryText || "All Parts",
          page: response?.page,
          totalPages: response?.nbPages,
        };
      }

      // Step 2: Fetch parts from Firestore using objectIDs
      const partsRef = collection(db, "parts");

      // Firestore `in` supports max 10 IDs at once, so batch if needed
      const batchedResults = [];
      for (let i = 0; i < ids.length; i += 10) {
        const batchIds = ids.slice(i, i + 10);
        const q = query(
          partsRef,
          where("__name__", "in", batchIds),
          orderBy("sortOrder", "asc"),
        );
        const snapshot = await getDocs(q);

        snapshot.forEach((doc) => {
          batchedResults.push({ id: doc.id, ...doc.data() });
        });
      }

      return {
        results: batchedResults,
        queryDisplay: queryText || "All Parts",
        page: response?.page,
        totalPages: response?.nbPages,
      };
    } catch (error) {
      console.error("Search Error:", error);
      toast({
        variant: "destructive",
        title: "Search Error",
        description: "Could not perform search.",
      });
      return rejectWithValue({ message: error.message });
    }
  },
);

export const savePart = createAsyncThunk(
  "parts/savePart",
  async ({ part, userId }, { rejectWithValue }) => {
    try {
      const savedPartsRef = collection(db, "savedParts");
      const q = query(
        savedPartsRef,
        where("partId", "==", part?.id),
        where("userId", "==", userId),
      );
      const existingDocs = await getDocs(q);

      if (!existingDocs.empty) {
        toast({
          title: "Info",
          description: "Part Already Saved...",
        });
        return;
      }
      const docRef = await addDoc(savedPartsRef, {
        part,
        partId: part?.id,
        userId,
        createdAt: new Date(),
      });
      toast({
        title: "Part Info",
        description: "Part Saved Successfully",
      });

      return { id: docRef.id, partId, userId };
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  },
);

export const removeSavedPart = createAsyncThunk(
  "parts/removeSavedPart",
  async ({ partId, userId }, { rejectWithValue }) => {
    try {
      const savedPartsRef = collection(db, "savedParts");
      const q = query(
        savedPartsRef,
        where("partId", "==", partId),
        where("userId", "==", userId),
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        for (const docSnap of querySnapshot.docs) {
          await deleteDoc(doc(db, "savedParts", docSnap.id));
        }
      }

      toast({
        title: "Part Info",
        description: "Part removed successfully.",
      });

      return { partId, userId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchSavedParts = createAsyncThunk(
  "parts/fetchSavedParts",
  async ({ userId }, { dispatch, rejectWithValue }) => {
    try {
      const savedPartsRef = collection(db, "savedParts");
      const q = query(savedPartsRef, where("userId", "==", userId));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const savedParts = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        dispatch(getSavedParts(savedParts));
      });

      return unsubscribe;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  },
);

export const fetchSinglePart = createAsyncThunk(
  "parts/fetchSinglePart",
  async (id, { rejectWithValue }) => {
    try {
      const partRef = doc(db, "parts", id);
      const partSnap = await getDoc(partRef);

      if (!partSnap.exists()) {
        return rejectWithValue("Part not found");
      }
      return { id: partSnap.id, ...partSnap.data() };
    } catch (error) {
      console.error("Error fetching part:", error);
      return rejectWithValue(error.message || "Failed to fetch part");
    }
  },
);

// Helper: convert a company name to its URL slug form
const toSlug = (name) => name.replace(/\s+/g, "-").toLowerCase();

export const getCompanyWithParts = createAsyncThunk(
  "vendor/getCompanyWithParts",
  async (slugOrName, { rejectWithValue }) => {
    try {
      const userRef = collection(db, "users");

      // 1. Try exact match first (handles calls that pass the real company name)
      let userDoc = null;
      const exactQuery = query(userRef, where("companyName", "==", slugOrName));
      const exactSnapshot = await getDocs(exactQuery);

      if (!exactSnapshot.empty) {
        userDoc = exactSnapshot.docs[0];
      } else {
        // 2. Slug-based fallback: fetch sellers and match by slugified companyName
        //    This handles direct URL visits where we only have the slug
        const incomingSlug = toSlug(slugOrName);
        const sellersQuery = query(userRef, where("role", "==", "seller"));
        const sellersSnapshot = await getDocs(sellersQuery);

        const matched = sellersSnapshot.docs.find((d) => {
          const name = d.data().companyName;
          return name && toSlug(name) === incomingSlug;
        });

        if (!matched) {
          return rejectWithValue("Company not found.");
        }
        userDoc = matched;
      }

      const userData = { id: userDoc.id, ...userDoc.data() };

      const partsRef = collection(db, "parts");
      const partsSnapshot = await getDocs(partsRef);

      const companyParts = [];
      partsSnapshot.forEach((partDoc) => {
        const partData = partDoc.data();
        if (partData.sellerId && partData?.sellerId === userData?.id) {
          companyParts.push({ id: partDoc.id, ...partData });
        }
      });

      return {
        company: userData,
        parts: companyParts,
      };
    } catch (error) {
      console.error("Error fetching company data:", error);
      return rejectWithValue(error.message);
    }
  },
);

export const fetchCategoriesAndMakes = createAsyncThunk(
  "parts/fetchCategoriesAndMakes",
  async (_, { rejectWithValue }) => {
    try {
      const categoriesSnap = await getDocs(collection(db, "partCategories"));
      const categories = categoriesSnap.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .sort((a, b) =>
          a.name?.toLowerCase().localeCompare(b.name?.toLowerCase()),
        );

      const makesSnap = await getDocs(collection(db, "partMakes"));
      const makes = makesSnap.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .sort((a, b) =>
          a.name?.toLowerCase().localeCompare(b.name?.toLowerCase()),
        );

      return { categories, makes };
    } catch (error) {
      console.error("Error fetching categories or makes:", error);
      return rejectWithValue(error.message);
    }
  },
);

export const fetchPartNamesByCategoryId = createAsyncThunk(
  "parts/fetchPartNamesByCategoryId",
  async (categoryId, { rejectWithValue }) => {
    try {
      if (!categoryId) {
        throw new Error("Category ID is required");
      }

      const q = query(
        collection(db, "partName"),
        where("categoryId", "==", categoryId),
      );

      const querySnapshot = await getDocs(q);

      const partNames = querySnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .sort((a, b) =>
          a.name?.toLowerCase().localeCompare(b.name?.toLowerCase()),
        );

      return partNames;
    } catch (error) {
      console.error("Error fetching part names:", error);
      return rejectWithValue(error.message);
    }
  },
);

export const fetchModelsByMake = createAsyncThunk(
  "parts/fetchModelsByMake",
  async (makeId, { rejectWithValue }) => {
    try {
      if (!makeId) {
        throw new Error("Make ID is required");
      }

      const q = query(
        collection(db, "partModels"),
        where("makeId", "==", makeId),
      );

      const querySnapshot = await getDocs(q);

      const models = querySnapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .sort((a, b) =>
          a.name?.toLowerCase().localeCompare(b.name?.toLowerCase()),
        );

      return models;
    } catch (error) {
      console.error("Error fetching models:", error);
      return rejectWithValue(error.message);
    }
  },
);

export const fetchSellerProfile = createAsyncThunk(
  "parts/fetchSellerProfile",
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) throw new Error("userId is required");

      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        return null;
      }

      return { id: userSnap.id, ...userSnap.data() };
    } catch (error) {
      console.error("Error fetching seller profile:", error);
      return rejectWithValue(error.message);
    }
  },
);
